import { Fragment } from 'react';
import { Key } from './Key.js';
import { accessibleKeyName } from '../key-names.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import Text from '../../Text/Text.js';

const keySortPriorities = {
  control: 1,
  meta: 2,
  alt: 3,
  option: 4,
  shift: 5,
  function: 6
};
const keySortPriority = priority => {
  var _keySortPriorities$pr;
  return (_keySortPriorities$pr = keySortPriorities[priority]) !== null && _keySortPriorities$pr !== void 0 ? _keySortPriorities$pr : Infinity;
};
const compareLowercaseKeys = (a, b) => keySortPriority(a) - keySortPriority(b);

/** Split and sort the chord keys in standard order. */
const splitChord = chord => chord.split('+').map(k => k.toLowerCase()).sort(compareLowercaseKeys);
const backgroundColors = {
  normal: 'var(--bgColor-transparent)',
  onEmphasis: 'var(--counter-bgColor-emphasis)',
  onPrimary: 'var(--button-primary-bgColor-active)'
};
const Chord = ({
  keys,
  format = 'condensed',
  variant = 'normal',
  size = 'normal'
}) => /*#__PURE__*/jsx(Text, {
  sx: {
    display: 'inline-flex',
    bg: backgroundColors[variant],
    color: variant === 'normal' ? 'var(--fgColor-muted)' : 'var(--fgColor-onEmphasis)',
    border: '1px solid',
    borderColor: variant === 'normal' ? 'var(--borderColor-default)' : 'transparent',
    borderRadius: size === 'small' ? 1 : 2,
    fontWeight: 'normal',
    fontFamily: 'normal',
    fontSize: size === 'small' ? '11px' : 0,
    p: size === 'small' ? '2px' : 1,
    gap: '0.5ch',
    boxShadow: 'none',
    verticalAlign: 'baseline',
    overflow: 'hidden',
    lineHeight: size === 'small' ? '8px' : '10px',
    minWidth: size === 'small' ? 'var(--base-size-16)' : 'var(--base-size-20)',
    justifyContent: 'center'
  },
  children: splitChord(keys).map((k, i) => /*#__PURE__*/jsxs(Fragment, {
    children: [i > 0 && format === 'full' ? /*#__PURE__*/jsx("span", {
      "aria-hidden": true,
      children: " + "
    }) // hiding the plus sign helps screen readers be more concise
    : ' ' // space is nonvisual due to flex layout but critical for labelling / screen readers
    , /*#__PURE__*/jsx(Key, {
      name: k,
      format: format
    })]
  }, i))
});
Chord.displayName = "Chord";
/** Plain string version of `Chord` for use in `aria` string attributes. */
const accessibleChordString = (chord, isMacOS) => splitChord(chord).map(key => accessibleKeyName(key, isMacOS)).join(' ');

export { Chord, accessibleChordString };
